import React from 'react';
import styled, { css } from 'styled-components';
import { typography, transitions } from '../../theme/tokens';

// Enums for text properties
export enum TextSize {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
  XXL = 'xxl'
}

export enum FontWeight {
  LIGHT = 300,
  REGULAR = 400,
  MEDIUM = 500,
  SEMIBOLD = 600,
  BOLD = 700,
  EXTRABOLD = 800
}

export enum TextAlign {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
  JUSTIFY = 'justify'
}

export enum TextTransform {
  NONE = 'none',
  CAPITALIZE = 'capitalize',
  UPPERCASE = 'uppercase',
  LOWERCASE = 'lowercase'
}

// Interface for component props
interface TextProps {
  size?: TextSize;
  weight?: FontWeight;
  align?: TextAlign;
  transform?: TextTransform;
  color?: string;
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}

// Styled component
const StyledText = styled.p<Omit<TextProps, 'children' | 'as' | 'className'>>`
  margin: 0;
  padding: 0;
  
  ${({ size = TextSize.MD }) => css`
    font-size: ${typography.fontSize[size]};
  `}

  ${({ weight = FontWeight.REGULAR }) => css`
    font-weight: ${weight};
  `}

  ${({ align = TextAlign.LEFT }) => css`
    text-align: ${align};
  `}

  ${({ transform = TextTransform.NONE }) => css`
    text-transform: ${transform};
  `}

  ${({ color }) => color && css`
    color: ${color};
  `}

  transition: color ${transitions.duration.normal} ${transitions.timing};
`;

export const Text: React.FC<TextProps> = ({
  size = TextSize.MD,
  weight = FontWeight.REGULAR,
  align = TextAlign.LEFT,
  transform = TextTransform.NONE,
  color,
  as = 'p',
  className,
  children,
  onClick,
}) => {
  return (
    <StyledText
      size={size}
      weight={weight}
      align={align}
      transform={transform}
      color={color}
      as={as}
      className={className}
      onClick={onClick}
    >
      {children}
    </StyledText>
  );
};

// Default export
export default Text;
